import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { 
    FiDollarSign, FiPlus, FiEye, FiTrash2, FiSearch,
    FiList, FiGrid, FiFile, FiFileText, FiCalendar,
    FiClock, FiCheckCircle, FiAlertCircle, FiX
} from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PinModal from '../components/PinModal';

const Debts = () => {
    const { user } = useAuth();
    const [debts, setDebts] = useState([]);
    const [filteredDebts, setFilteredDebts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedDebt, setSelectedDebt] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [debtToDelete, setDebtToDelete] = useState(null);
    const [paymentData, setPaymentData] = useState({
        amount: '',
        payment_method: 'cash',
        reference: '',
        notes: ''
    });
    const [stats, setStats] = useState({
        total_debts: 0,
        total_remaining: 0,
        total_amount: 0,
        pending_count: 0,
        overdue_count: 0,
        partial_count: 0
    });

    const isAdmin = user?.role === 'admin';
    const isCaissier = user?.role === 'caissier';
    const canManage = isAdmin || isCaissier;

    useEffect(() => {
        if (canManage) {
            fetchDebts();
            fetchStats();
        }
    }, [statusFilter]);

    useEffect(() => {
        filterDebts();
    }, [search, debts]);

    const fetchDebts = async () => {
        try {
            const response = await api.get(`/debts?status=${statusFilter !== 'all' ? statusFilter : ''}`);
            setDebts(response.data);
            setFilteredDebts(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des dettes');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.get('/debts/stats/summary');
            setStats(response.data);
        } catch (error) {
            console.error('Erreur stats:', error);
        }
    };

    const filterDebts = () => {
        let filtered = [...debts];
        if (search) {
            filtered = filtered.filter(debt => 
                debt.client_name?.toLowerCase().includes(search.toLowerCase()) ||
                debt.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
                debt.client_phone?.includes(search)
            );
        }
        setFilteredDebts(filtered);
    };

    const viewDetails = async (debtId) => {
        try {
            const response = await api.get(`/debts/${debtId}`);
            setSelectedDebt(response.data);
            setShowDetailModal(true);
        } catch (error) {
            toast.error('Erreur lors du chargement des détails');
        }
    };

    const handlePayment = async (e) => {
        e.preventDefault();
        if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
            toast.error('Montant invalide');
            return;
        }
        if (parseFloat(paymentData.amount) > selectedDebt.remaining_amount) {
            toast.error('Le montant dépasse le reste à payer');
            return;
        }

        try {
            const response = await api.post(`/debts/${selectedDebt.id}/payments`, paymentData);
            toast.success('Paiement enregistré avec succès');
            setShowPaymentModal(false);
            setPaymentData({ amount: '', payment_method: 'cash', reference: '', notes: '' });
            fetchDebts();
            fetchStats();
            if (selectedDebt) {
                viewDetails(selectedDebt.id);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erreur lors du paiement');
        }
    };

    const handleDelete = async () => {
        if (!debtToDelete) return;
        try {
            await api.delete(`/debts/${debtToDelete.id}`);
            toast.success('Dette supprimée');
            setShowPinModal(false);
            setDebtToDelete(null);
            fetchDebts();
            fetchStats();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount || 0);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800',
            partial: 'bg-blue-100 text-blue-800',
            paid: 'bg-green-100 text-green-800',
            overdue: 'bg-red-100 text-red-800'
        };
        const labels = {
            pending: 'En attente',
            partial: 'Partiel',
            paid: 'Payé',
            overdue: 'En retard'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
                {labels[status] || status}
            </span>
        );
    };

    if (!canManage) {
        return (
            <div className="text-center py-12">
                <FiAlertCircle className="mx-auto text-red-500" size={48} />
                <p className="text-gray-500 mt-2">Vous n'avez pas accès à cette page</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">💰 Gestion des dettes</h1>
                    <p className="text-sm text-gray-500">Suivez et gérez les dettes clients</p>
                </div>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="card text-center">
                    <p className="text-sm text-gray-500">Total dettes</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.total_debts || 0}</p>
                </div>
                <div className="card text-center">
                    <p className="text-sm text-gray-500">Montant total</p>
                    <p className="text-2xl font-bold text-green-600">{formatAmount(stats.total_amount)}</p>
                </div>
                <div className="card text-center">
                    <p className="text-sm text-gray-500">Reste à payer</p>
                    <p className="text-2xl font-bold text-red-600">{formatAmount(stats.total_remaining)}</p>
                </div>
                <div className="card text-center">
                    <p className="text-sm text-gray-500">En retard</p>
                    <p className="text-2xl font-bold text-red-600">{stats.overdue_count || 0}</p>
                </div>
            </div>

            {/* Filtres */}
            <div className="card mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher client ou facture..."
                            className="input-field pl-10"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="input-field"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">Tous les statuts</option>
                        <option value="pending">En attente</option>
                        <option value="partial">Partiel</option>
                        <option value="overdue">En retard</option>
                        <option value="paid">Payé</option>
                    </select>
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                setStatusFilter('overdue');
                            }}
                            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                        >
                            ⚠️ En retard
                        </button>
                    </div>
                </div>
            </div>

            {/* Liste des dettes */}
            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="border-b">
                        <tr className="text-left">
                            <th className="pb-3">Client</th>
                            <th className="pb-3">Facture</th>
                            <th className="pb-3 text-right">Montant</th>
                            <th className="pb-3 text-right">Payé</th>
                            <th className="pb-3 text-right">Reste</th>
                            <th className="pb-3 text-center">Échéance</th>
                            <th className="pb-3 text-center">Statut</th>
                            <th className="pb-3 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDebts.map((debt) => (
                            <tr key={debt.id} className="border-b hover:bg-gray-50 transition-colors">
                                <td className="py-3 font-medium">{debt.client_name}</td>
                                <td className="py-3 text-sm">{debt.invoice_number || '-'}</td>
                                <td className="py-3 text-right font-medium">{formatAmount(debt.amount)}</td>
                                <td className="py-3 text-right text-green-600">{formatAmount(debt.paid_amount)}</td>
                                <td className="py-3 text-right font-bold text-red-600">{formatAmount(debt.remaining_amount)}</td>
                                <td className="py-3 text-center">
                                    <span className={`text-sm ${debt.due_date && new Date(debt.due_date) < new Date() && debt.status !== 'paid' ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                                        {formatDate(debt.due_date)}
                                    </span>
                                </td>
                                <td className="py-3 text-center">{getStatusBadge(debt.status)}</td>
                                <td className="py-3 text-center">
                                    <div className="flex justify-center gap-2">
                                        <button
                                            onClick={() => viewDetails(debt.id)}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                            title="Voir détails"
                                        >
                                            <FiEye size={16} />
                                        </button>
                                        {debt.status !== 'paid' && (
                                            <button
                                                onClick={() => {
                                                    setSelectedDebt(debt);
                                                    setPaymentData({ amount: '', payment_method: 'cash', reference: '', notes: '' });
                                                    setShowPaymentModal(true);
                                                }}
                                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                                title="Enregistrer paiement"
                                            >
                                                <FiDollarSign size={16} />
                                            </button>
                                        )}
                                        {isAdmin && (
                                            <button
                                                onClick={() => {
                                                    setDebtToDelete(debt);
                                                    setShowPinModal(true);
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                title="Supprimer"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredDebts.length === 0 && (
                    <div className="text-center py-12">
                        <FiDollarSign className="mx-auto text-gray-300" size={48} />
                        <p className="text-gray-500 mt-2">Aucune dette trouvée</p>
                    </div>
                )}
            </div>

            {/* Modal Détails dette */}
            {showDetailModal && selectedDebt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Détails de la dette</h2>
                            <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                <FiX size={20} />
                            </button>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Client</p>
                                    <p className="font-semibold">{selectedDebt.client_name}</p>
                                    <p className="text-sm text-gray-600">{selectedDebt.client_phone}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Facture</p>
                                    <p className="font-semibold">{selectedDebt.invoice_number || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Montant total</p>
                                    <p className="font-bold text-green-600">{formatAmount(selectedDebt.amount)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Reste à payer</p>
                                    <p className="font-bold text-red-600">{formatAmount(selectedDebt.remaining_amount)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Échéance</p>
                                    <p className={`font-semibold ${selectedDebt.due_date && new Date(selectedDebt.due_date) < new Date() && selectedDebt.status !== 'paid' ? 'text-red-600' : ''}`}>
                                        {formatDate(selectedDebt.due_date)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Statut</p>
                                    {getStatusBadge(selectedDebt.status)}
                                </div>
                            </div>
                            {selectedDebt.notes && (
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">Notes</p>
                                    <p className="text-sm">{selectedDebt.notes}</p>
                                </div>
                            )}
                        </div>

                        {selectedDebt.payments && selectedDebt.payments.length > 0 && (
                            <div>
                                <h3 className="font-semibold mb-2">Historique des paiements</h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="border-b">
                                            <tr>
                                                <th className="text-left py-2">Date</th>
                                                <th className="text-right py-2">Montant</th>
                                                <th className="text-left py-2">Méthode</th>
                                                <th className="text-left py-2">Référence</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedDebt.payments.map((payment) => (
                                                <tr key={payment.id} className="border-b">
                                                    <td className="py-2">{formatDate(payment.created_at)}</td>
                                                    <td className="py-2 text-right text-green-600">{formatAmount(payment.amount)}</td>
                                                    <td className="py-2">{
                                                        payment.payment_method === 'cash' ? 'Espèces' :
                                                        payment.payment_method === 'mobile_money' ? 'Mobile Money' :
                                                        payment.payment_method === 'bank_transfer' ? 'Virement' : 'Chèque'
                                                    }</td>
                                                    <td className="py-2">{payment.reference || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {selectedDebt.status !== 'paid' && (
                            <button
                                onClick={() => {
                                    setShowDetailModal(false);
                                    setShowPaymentModal(true);
                                }}
                                className="mt-4 w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                            >
                                Enregistrer un paiement
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Modal Paiement */}
            {showPaymentModal && selectedDebt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Enregistrer un paiement</h2>
                            <button onClick={() => setShowPaymentModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                <FiX size={20} />
                            </button>
                        </div>

                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <p><strong>Client:</strong> {selectedDebt.client_name}</p>
                            <p><strong>Reste à payer:</strong> <span className="text-red-600 font-bold">{formatAmount(selectedDebt.remaining_amount)}</span></p>
                        </div>

                        <form onSubmit={handlePayment}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Montant *</label>
                                <input
                                    type="number"
                                    className="input-field"
                                    placeholder="Montant à payer"
                                    value={paymentData.amount}
                                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                                    required
                                    min="1"
                                    max={selectedDebt.remaining_amount}
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                                <select
                                    className="input-field"
                                    value={paymentData.payment_method}
                                    onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                                >
                                    <option value="cash">Espèces</option>
                                    <option value="mobile_money">Mobile Money</option>
                                    <option value="bank_transfer">Virement</option>
                                    <option value="check">Chèque</option>
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                                <input
                                    type="text"
                                    className="input-field"
                                    placeholder="Numéro de transaction"
                                    value={paymentData.reference}
                                    onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                                />
                            </div>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    className="input-field"
                                    rows="2"
                                    placeholder="Notes sur le paiement"
                                    value={paymentData.notes}
                                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                                />
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setShowPaymentModal(false)} className="flex-1 btn-secondary">
                                    Annuler
                                </button>
                                <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700">
                                    Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <PinModal
                isOpen={showPinModal}
                onClose={() => {
                    setShowPinModal(false);
                    setDebtToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Supprimer une dette"
                message="Êtes-vous sûr de vouloir supprimer cette dette ? Cette action est irréversible."
            />
        </div>
    );
};

export default Debts;